import { ShoppingCart, Sparkles, ArrowRight, Check, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const InfusoShop = () => {
  const products = [
    {
      id: 1,
      name: "Infuso Soda Machine",
      description: "Complete carbonation and infusion system with CO₂ cylinder",
      image: "https://static.prod-images.emergentagent.com/jobs/3c7d4d91-69a4-4dbc-840a-7249f347f396/images/a93ab32367234ddd2bfdcce6b2f8536bb368d5de13a5b3a83d267fcdc25eb403.jpeg",
      price: "£50.00",
      link: "https://infuso.com/shop/",
      features: ["CO₂ carbonation", "N₂O infusion capable", "640g cylinder included", "Easy to use"],
      badge: "Best Seller"
    },
    {
      id: 2,
      name: "CO₂ 640g Cylinder",
      description: "Food-grade CO₂ for perfect carbonation of herbal beverages",
      image: "https://static.prod-images.emergentagent.com/jobs/3c7d4d91-69a4-4dbc-840a-7249f347f396/images/d1eb9d287f9f5568618dee7d3de541661b3daa2aed181157c1dd2b8508fe0d7d.jpeg",
      price: "£24.95",
      link: "https://infuso.com/shop/all/infuso-n2o-640g-single-cylinder/",
      features: ["640g capacity", "Food-grade CO₂", "Makes 60L sparkling water", "Compatible with Infuso"],
      badge: null
    },
    {
      id: 3,
      name: "N₂O 640g Cylinder",
      description: "For rapid infusion of herbs and flavors",
      image: "https://static.prod-images.emergentagent.com/jobs/3c7d4d91-69a4-4dbc-840a-7249f347f396/images/d1eb9d287f9f5568618dee7d3de541661b3daa2aed181157c1dd2b8508fe0d7d.jpeg",
      price: "£24.95",
      link: "https://infuso.com/shop/all/infuso-n2o-640g-single-cylinder/",
      features: ["640g capacity", "Rapid infusion", "Extract flavors quickly", "Safe for food use"],
      badge: null
    }
  ];

  const teaProducts = [
    {
      name: "Apple & Cranberry",
      image: "https://static.prod-images.emergentagent.com/jobs/3c7d4d91-69a4-4dbc-840a-7249f347f396/images/d7b026d19b9cc20058e65899f400acf09d0f85544ba2e4d6ab6055447fdc667c.jpeg",
      description: "Sweet and tart organic fruit tea blend",
      link: "https://infuso.com/shop/infuso-fruit-teas/"
    },
    {
      name: "Caribbean Christmas",
      image: "https://static.prod-images.emergentagent.com/jobs/3c7d4d91-69a4-4dbc-840a-7249f347f396/images/45721365a0f51ce5ab7a823722cdd96e8cb51701bcba379f87c3401d45f8ee94.jpeg",
      description: "Festive tropical herbal infusion",
      link: "https://infuso.com/shop/infuso-fruit-teas/"
    },
    {
      name: "Chilli Christmas",
      image: "https://static.prod-images.emergentagent.com/jobs/3c7d4d91-69a4-4dbc-840a-7249f347f396/images/45721365a0f51ce5ab7a823722cdd96e8cb51701bcba379f87c3401d45f8ee94.jpeg",
      description: "Spicy seasonal blend with warming herbs",
      link: "https://infuso.com/shop/infuso-fruit-teas/"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-emerald-50/30 to-white">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-primary to-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-5 h-5" />
            <span className="font-semibold">Official Infuso Partner</span>
          </div>
          <h1 className="font-playfair text-5xl md:text-6xl font-bold mb-4">
            Carbonate Your Herbs
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
            Everything you need to create sparkling herbal beverages at home
          </p>
        </div>
      </section>

      {/* Main Products */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-4xl font-bold text-gray-900 mb-4">
              Carbonation Equipment
            </h2>
            <p className="text-xl text-gray-600">
              Professional-grade equipment for sparkling herbal drinks
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {products.map((product) => (
              <div key={product.id} className="bg-white rounded-2xl shadow-xl border-2 border-gray-100 overflow-hidden hover:shadow-2xl hover:border-primary/30 transition-all">
                {product.badge && (
                  <div className="bg-gradient-to-r from-primary to-emerald-600 text-white text-center py-2 font-semibold text-sm">
                    {product.badge}
                  </div>
                )}
                
                <div className="p-6">
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 mb-6 flex items-center justify-center">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-48 h-48 object-contain"
                    />
                  </div>

                  <h3 className="font-playfair text-2xl font-bold text-gray-900 mb-2">
                    {product.name}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {product.description}
                  </p>

                  <div className="mb-4">
                    <p className="text-3xl font-bold text-primary">
                      {product.price}
                    </p>
                  </div>

                  <div className="space-y-2 mb-6">
                    {product.features.map((feature, index) => (
                      <div key={`${feature}-${index}`} className="flex items-center text-sm text-gray-700">
                        <Check className="w-4 h-4 text-primary mr-2 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <a
                    href={product.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full"
                  >
                    <Button className="w-full h-12 rounded-full bg-gradient-to-r from-primary to-emerald-600 hover:from-emerald-600 hover:to-primary text-white font-semibold">
                      <ShoppingCart className="mr-2 w-5 h-5" />
                      Buy Now
                      <ExternalLink className="ml-2 w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fruit Teas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-4xl font-bold text-gray-900 mb-4">
              Premium Organic Fruit Teas
            </h2>
            <p className="text-xl text-gray-600">
              Specially selected herbal blends perfect for carbonation
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {teaProducts.map((tea, index) => (
              <div key={`${tea.name || tea.title || "tea"}-${index}`} className="group">
                <div className="relative overflow-hidden rounded-2xl shadow-lg border-2 border-gray-200 mb-4 bg-white hover:border-primary/50 transition-all">
                  <img
                    src={tea.image}
                    alt={tea.name}
                    className="w-full h-64 object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="font-playfair text-2xl font-bold text-white mb-2">
                      {tea.name}
                    </h3>
                    <p className="text-white/90 text-sm mb-4">
                      {tea.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <a
              href="https://infuso.com/shop/infuso-fruit-teas/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" className="h-14 px-8 rounded-full bg-gradient-to-r from-primary to-emerald-600 hover:from-emerald-600 hover:to-primary text-white shadow-lg text-lg font-semibold">
                <ShoppingCart className="mr-2 w-5 h-5" />
                Shop All Fruit Teas
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Infuso */}
      <section className="py-20 bg-gradient-to-b from-emerald-50/50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-4xl font-bold text-gray-900 mb-4">
              Why Choose Infuso?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Professional Quality</h3>
              <p className="text-gray-600 text-sm">Commercial-grade equipment for home use</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Easy to Use</h3>
              <p className="text-gray-600 text-sm">Simple operation, perfect results every time</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Safe & Certified</h3>
              <p className="text-gray-600 text-sm">Food-grade materials, EU certified</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingCart className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Direct from Infuso</h3>
              <p className="text-gray-600 text-sm">Authentic products, best prices</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-playfair text-4xl md:text-5xl font-bold mb-6">
            Start Creating Sparkling Herbal Drinks Today
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get personalized carbonated herbal recommendations with our AI guide
          </p>
          <a href="/questionnaire?mode=carbonated">
            <Button size="lg" className="h-16 px-10 rounded-full bg-white text-primary hover:bg-white/90 shadow-2xl text-lg font-semibold">
              <Sparkles className="mr-2 w-6 h-6" />
              Get Carbonated Recommendations
              <ArrowRight className="ml-2 w-6 h-6" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default InfusoShop;
